import AnalyticCard from '@/components/Analytic/Card'
import AnalyticsIcons from '@/icons/AnalyticsIcons'
import ChartIcons from '@/icons/ChartIcons'
import WorldPublicIcons from '@/icons/WorldPublicIcons'
import {
    formatBandwidthUnit,
    formatVolumeGB,
    formatVolumeUnit,
    max,
} from '@/utils/function'
import React from 'react'
import { Bandwidth, Regions, Volume } from '../../types'
import Paper from '@/components/Paper'
import { Typogaphy } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'

interface StatisticalProps {
    bandwidth: Bandwidth
    volume: Volume
    regions: Regions
}

// Region breakdown mirrors the partner "Business Usage" view (/services/cdn/usage):
// Domestic(VN, incl untagged) / International / China / Taiwan / Thailand — the SAME
// billing-basis split servicemgmt returns from /cdn/regions, so the monitoring cards
// never diverge from what is billed. No client-side country grouping.
const Statistical: React.FC<StatisticalProps> = ({
    bandwidth,
    volume,
    regions,
}) => {
    const { t } = useTranslation()
    const maxBandwidth = max(bandwidth?.data?.map((item) => item.value)) || 0

    const r = regions || { local: 0, global: 0, cn: 0, tw: 0, th: 0 }
    const totalVolumes = r.local + r.global + r.cn + r.tw + r.th
    // Fallback: if the regions endpoint is empty (old servicemgmt), keep showing the
    // volume series total so the card is never blank.
    const totalDisplay =
        totalVolumes > 0
            ? totalVolumes
            : (volume?.data || []).reduce((s, v) => s + (v?.value || 0), 0)

    const trafficData = [
        { label: t('services.local'), value: r.local },
        { label: t('services.global'), value: r.global },
        { label: t('services.cn'), value: r.cn },
        { label: t('services.tw'), value: r.tw },
        { label: 'Băng thông Thailand', value: r.th },
    ]

    return (
        <div className="grid xs:grid-cols-1 md:grid-cols-3 gap-2">
            <AnalyticCard
                title={formatBandwidthUnit(maxBandwidth)}
                subtitle="max-bandwidth"
                icon={<AnalyticsIcons />}
                text={[
                    {
                        prefix: 'average-bandwidth',
                        suffix: formatBandwidthUnit(
                            bandwidth?.averageThroughput
                        ),
                    },
                ]}
            />
            <AnalyticCard
                title={formatVolumeUnit(totalDisplay)}
                subtitle="volume"
                icon={<ChartIcons />}
                text={[
                    {
                        prefix: 'total-volume',
                        // Số GB chính xác để đối soát (đơn vị rút gọn PB/TB làm tròn
                        // che mất hàng chục TB chênh lệch).
                        suffix: formatVolumeGB(totalDisplay),
                    },
                ]}
            />
            <Paper className="p-4">
                <div className="flex justify-between items-center">
                    <div>
                        <Typogaphy className="text-2xl font-semibold text-gray-900">
                            {t('services.traffics')}
                        </Typogaphy>
                        <p className="text-gray-500 text-sm mt-1">
                            {t('services.traffics-description')}
                        </p>
                    </div>
                    <div className="text-blue-600 w-8 h-8">
                        <WorldPublicIcons />
                    </div>
                </div>

                <div className="mt-3 space-y-2">
                    {trafficData.map((traffic, index) => (
                        <div
                            key={index}
                            className="flex justify-between items-center"
                        >
                            <span className="text-sm text-gray-600">
                                {traffic.label}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                                {formatVolumeGB(traffic.value)}
                            </span>
                        </div>
                    ))}
                </div>
            </Paper>
        </div>
    )
}

export default Statistical
